"use client";
import React, { useState } from "react";
import { CURRENT_TERMS_VERSION } from "@shared/constants/termsVersion";
import { useAuth } from "@/contexts/AuthContext";
import { apiService } from "@/services/api";
import Button from "../Button";

export function TermsUpdateModal() {
  const { user, token, refreshUser, updateUser, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accepted = user?.termsVersion === CURRENT_TERMS_VERSION;
  const visible = !!user && !!token && !accepted;

  if (!visible) return null;

  const handleAccept = async () => {
    if (!checked || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiService.acceptTerms(CURRENT_TERMS_VERSION, token);
      updateUser({
        termsVersion: res?.data?.termsVersion ?? CURRENT_TERMS_VERSION,
        termsAcceptedAt: res?.data?.termsAcceptedAt,
      });
      void refreshUser();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-[440px] bg-white rounded-[8px] border-asymmetric border-[#151515] shadow-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold fraunces-display text-[#111827]">
            We&apos;ve updated our safety policies
          </h2>
        </div>
        <div className="px-5 py-4 space-y-3 max-h-[50vh] overflow-y-auto text-sm text-gray-700 space-mono-text leading-relaxed">
          <p>
            To keep Joydrop welcoming and safe, we&apos;ve updated our policies. Please review and
            accept them to continue.
          </p>
          <p>
            Joydrop has zero tolerance for objectionable content or abusive users. You can report
            or block anyone whose content violates our{" "}
            <a
              href="/guidelines"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#421E7A] underline"
            >
              Community Guidelines
            </a>
            . Our moderation team responds within 24 hours.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-gray-100">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#421E7A] cursor-pointer flex-shrink-0"
            />
            <span className="text-xs leading-snug text-gray-700 space-mono-text">
              I agree to the{" "}
              <a
                href="/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#421E7A] underline"
              >
                Terms of Use
              </a>
              ,{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#421E7A] underline"
              >
                Privacy Policy
              </a>
              , and{" "}
              <a
                href="/guidelines"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#421E7A] underline"
              >
                Community Guidelines
              </a>
              .
            </span>
          </label>
        </div>
        {error ? (
          <div className="px-5 pb-2 text-xs text-red-600 space-mono-text">{error}</div>
        ) : null}
        <div className="px-5 pb-5 flex flex-col gap-2">
          <Button
            variant="primary"
            onClick={handleAccept}
            disabled={!checked || submitting}
            className="w-full"
          >
            {submitting ? "Saving..." : "Accept and Continue"}
          </Button>
          <button
            type="button"
            onClick={() => void logout()}
            className="text-xs text-gray-500 hover:text-gray-700 py-2 self-center"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
